import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Line, Bar } from 'react-chartjs-2';
import { Table, Alert, Input } from 'reactstrap';

import { fetchExternalApiOverview, OVERVIEW_TOP_CLIENTS, OTHER_COLOR, clientDisplayName } from '../helpers/externalApiOverview';
import { authMethodLabel } from '../helpers/externalApiStats';
import { formatNumber } from '../helpers/format';
import { formatPoints, formatLoadPercent, loadBadgeClass } from '../helpers/externalApiCost';

/**
* "Overview" tab of the global API analytics page (VD-725): long periods (up
* to a year) from the daily counters behind `/overview`, compared to the
* previous period of the same length.
*
* Color: the daily line is one series (sequential default blue, the previous
* period as a muted dashed line beside it); the "who uses it" stack is
* identity, so each client takes its categorical slot from the page-level
* registry (`colorFor`) - same client, same color as on the Load tab - and
* the rest folds into a neutral "Other". Up/down deltas use the reserved
* status colors, always together with an arrow and the signed number.
*/

const palette = {
    surface: '#FFFFFF',
    border: '#E6EDF5',
    textPrimary: '#3A3F56',
    textSecondary: '#52514E',
    textMuted: '#818FA5',
    blue: '#2a78d6',
    good: '#0ca30c',
    critical: '#d03b3b',
};

const styles = {
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 },
    tile: { backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)' },
    tileLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
    tileValue: { fontSize: 24, fontWeight: 600, color: palette.textPrimary, lineHeight: 1.1 },
    tileSub: { fontSize: 12, color: palette.textSecondary, marginTop: 4 },
    card: { backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20 },
    cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: palette.textPrimary },
    note: { fontSize: 12, color: palette.textMuted, marginTop: 6 },
    legendRow: { display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: palette.textSecondary, background: 'transparent', border: 'none', padding: 0 },
    swatch: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
    badge: { display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
    shareTrack: { height: 4, borderRadius: 2, backgroundColor: palette.border, marginTop: 4, maxWidth: 90 },
    progressTrack: { height: 10, borderRadius: 5, backgroundColor: palette.border, overflow: 'hidden', margin: '10px 0 14px 0' },
    link: { background: 'transparent', border: 'none', padding: 0, color: '#33405E', textDecoration: 'underline', cursor: 'pointer', textAlign: 'left' },
    emptyState: { textAlign: 'center', padding: '40px 20px', color: palette.textMuted, fontSize: 14 },
};

const BADGES = {
    heavy: { backgroundColor: '#FDE8E8', color: '#a12b2b' },
    new: { backgroundColor: '#E3EEFB', color: '#1c5cab' },
    basic: { backgroundColor: '#FFF1E0', color: '#8a4b00' },
    stopped: { backgroundColor: '#EDEDEA', color: '#5f5e5a' },
};

const fmtDecimal = (v, digits = 1) => (v === null || v === undefined || isNaN(Number(v)) ? '-' : Number(v).toFixed(digits));

/** signed delta with an arrow; `inverse` = an increase is bad (errors) */
function Delta({ value, suffix = '%', inverse = false }) {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return <span style={{ color: palette.textMuted }}>–</span>;
    }

    let n = Number(value);
    if (n === 0) {
        return <span style={{ color: palette.textMuted }}>0{suffix}</span>;
    }

    let good = inverse ? n < 0 : n > 0;
    let text = suffix === '%' ? `${Math.abs(n).toFixed(1)}%` : formatNumber(Math.abs(n));

    return (
        <span style={{ color: good ? palette.good : palette.critical, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {n > 0 ? '▲' : '▼'} {n > 0 ? '+' : '-'}{text}
        </span>
    );
}

function KpiTile({ label, value, delta, sub, title }) {
    return (
        <div style={styles.tile} title={title}>
            <div style={styles.tileLabel}>{label}</div>
            <div style={styles.tileValue}>{value}</div>
            {delta !== undefined || sub ? (
                <div style={styles.tileSub}>
                    {delta}
                    {delta !== undefined && sub ? ' · ' : null}
                    {sub}
                </div>
            ) : null}
        </div>
    );
}

const DAILY_METRICS = [
    { key: 'requests', label: 'Requests' },
    { key: 'points', label: 'Points' },
    { key: 'errors', label: 'Errors' },
    { key: 'activeClients', label: 'Active clients' },
];

function DailyChart({ daily, previousDaily, metric, lang }) {
    let rows = daily || [];
    let labels = rows.map((d) => moment.utc(d.day).format(rows.length > 120 ? 'YYYY-MM' : 'MM-DD'));
    let metricLabel = (DAILY_METRICS.find((m) => m.key === metric) || DAILY_METRICS[0]).label.translate(lang);

    let datasets = [{
        label: metricLabel,
        data: rows.map((d) => d[metric] || 0),
        borderColor: palette.blue,
        backgroundColor: 'rgba(42, 120, 214, 0.12)',
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.15,
    }];

    if (previousDaily && previousDaily.length) {
        // aligned by position: day 1 of the previous period sits under day 1 of this one
        datasets.push({
            label: `${metricLabel} (${'previous period'.translate(lang)})`,
            data: rows.map((_, idx) => (previousDaily[idx] ? previousDaily[idx][metric] || 0 : null)),
            borderColor: palette.textMuted,
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [5, 4],
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.15,
        });
    }

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: datasets.length > 1, position: 'top', align: 'start', labels: { usePointStyle: true, boxWidth: 8, color: palette.textSecondary, font: { size: 12 } } },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (items) => (items[0] && rows[items[0].dataIndex] ? moment.utc(rows[items[0].dataIndex].day).format('YYYY-MM-DD') : ''),
                    afterBody: (items) => {
                        let d = items[0] ? rows[items[0].dataIndex] : null;
                        if (!d) return [];
                        return [
                            `${'Overloaded minutes'.translate(lang)}: ${formatNumber(d.overloadMinutes)}`,
                            `${'Worst single-client minute'.translate(lang)}: ${formatLoadPercent(d.peakClientLoadPercent)}`,
                            `${'Avg load (24 h)'.translate(lang)}: ${formatLoadPercent(d.avgLoadPercent)}`,
                        ];
                    },
                },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: palette.textMuted, maxRotation: 0, autoSkip: true, font: { size: 11 } } },
            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, precision: 0, font: { size: 11 } } },
        },
    };

    return (
        <div style={{ height: 280 }}>
            <Line data={{ labels, datasets }} options={options} />
        </div>
    );
}

function ClientStackChart({ dailyByClient, metric, colorFor, onOpenClient, lang }) {
    let clients = (dailyByClient && dailyByClient.clients) || [];
    let days = (dailyByClient && dailyByClient.days) || [];
    let labels = days.map((d) => moment.utc(d.day).format(days.length > 120 ? 'YYYY-MM' : 'MM-DD'));

    let datasets = clients.map((c) => ({
        label: clientDisplayName(c),
        data: days.map((d) => (d.clients && d.clients[c.clientId] ? d.clients[c.clientId][metric] || 0 : 0)),
        backgroundColor: colorFor(c.clientId),
        borderColor: palette.surface,
        borderWidth: { top: 1 },
        borderSkipped: false,
        stack: 'clients',
    }));

    datasets.push({
        label: 'Other'.translate(lang),
        data: days.map((d) => (d.other ? d.other[metric] || 0 : 0)),
        backgroundColor: OTHER_COLOR,
        borderColor: palette.surface,
        borderWidth: { top: 1 },
        borderSkipped: false,
        stack: 'clients',
    });

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            // the clickable legend is rendered below as HTML
            legend: { display: false },
            tooltip: {
                mode: 'index',
                intersect: false,
                itemSort: (a, b) => b.parsed.y - a.parsed.y,
                filter: (item) => item.parsed.y > 0,
                callbacks: {
                    title: (items) => (items[0] && days[items[0].dataIndex] ? moment.utc(days[items[0].dataIndex].day).format('YYYY-MM-DD') : ''),
                },
            },
        },
        scales: {
            x: { stacked: true, grid: { display: false }, ticks: { color: palette.textMuted, maxRotation: 0, autoSkip: true, font: { size: 11 } } },
            y: { stacked: true, beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, precision: 0, font: { size: 11 } } },
        },
    };

    return (
        <div>
            <div style={{ height: 300 }}>
                <Bar data={{ labels, datasets }} options={options} />
            </div>
            <div style={styles.legendRow}>
                {clients.map((c) => (
                    <button key={c.clientId} type="button" style={{ ...styles.legendItem, cursor: onOpenClient ? 'pointer' : 'default' }} onClick={() => onOpenClient && onOpenClient(c)}>
                        <span style={{ ...styles.swatch, backgroundColor: colorFor(c.clientId) }} />
                        {clientDisplayName(c)}
                    </button>
                ))}
                <span style={styles.legendItem}>
                    <span style={{ ...styles.swatch, backgroundColor: OTHER_COLOR }} />
                    {'Other'.translate(lang)}
                </span>
            </div>
        </div>
    );
}

const CLIENT_COLUMNS = [
    { key: 'name', label: 'Client', sort: (r) => clientDisplayName(r).toLowerCase() },
    { key: 'requests', label: 'Requests', sort: (r) => r.requests || 0 },
    { key: 'requestsChange', label: 'Δ %', sort: (r) => (r.change && r.change.requestsPercent !== null && r.change.requestsPercent !== undefined ? r.change.requestsPercent : -Infinity) },
    { key: 'points', label: 'Points', sort: (r) => r.points || 0 },
    { key: 'pointsChange', label: 'Δ %', sort: (r) => (r.change && r.change.pointsPercent !== null && r.change.pointsPercent !== undefined ? r.change.pointsPercent : -Infinity) },
    { key: 'sharePercent', label: 'Share %', sort: (r) => r.sharePercent || 0 },
    { key: 'avgPointsPerRequest', label: 'Avg points/req', sort: (r) => r.avgPointsPerRequest || 0 },
    { key: 'errorPercent', label: 'Errors %', sort: (r) => r.errorPercent || 0 },
    { key: 'peakLoadPercent', label: 'Peak load %', sort: (r) => r.peakLoadPercent || 0 },
    { key: 'overloadMinutes', label: 'Overloaded min', sort: (r) => r.overloadMinutes || 0 },
    { key: 'activeDays', label: 'Active days', sort: (r) => r.activeDays || 0 },
    { key: 'authMethod', label: 'Auth', sort: (r) => r.authMethod || '' },
    { key: 'lastUsedAt', label: 'Last used', sort: (r) => (r.lastUsedAt ? new Date(r.lastUsedAt).getTime() : 0) },
];

function ClientsTable({ clients, cost, onOpenClient, lang }) {
    let [search, setSearch] = useState('');
    let [sortKey, setSortKey] = useState('points');
    let [sortDir, setSortDir] = useState(-1);

    let rows = useMemo(() => {
        let text = search.trim().toLowerCase();
        let list = (clients || []).filter((r) => !text || [r.name, r.username, r.company, r.customerNumber]
            .some((v) => v && String(v).toLowerCase().indexOf(text) !== -1));
        let col = CLIENT_COLUMNS.find((c) => c.key === sortKey) || CLIENT_COLUMNS[3];

        return list.slice().sort((a, b) => {
            let va = col.sort(a);
            let vb = col.sort(b);
            if (va < vb) return -1 * sortDir;
            if (va > vb) return 1 * sortDir;
            return 0;
        });
    }, [clients, search, sortKey, sortDir]);

    let toggleSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => -d);
        } else {
            setSortKey(key);
            setSortDir(key === 'name' || key === 'authMethod' ? 1 : -1);
        }
    };

    return (
        <div style={styles.card}>
            <div style={styles.cardHeader}>
                <div style={styles.sectionTitle}>{'Clients'.translate(lang)} ({formatNumber(rows.length)})</div>
                <Input type="text" style={{ maxWidth: 260, height: 38 }} placeholder={'Search by name...'.translate(lang)} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Table responsive hover>
                <thead>
                    <tr>
                        {CLIENT_COLUMNS.map((c) => (
                            <th key={c.key} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort(c.key)}>
                                {c.label.translate(lang)} {sortKey === c.key ? (sortDir === 1 ? '▲' : '▼') : ''}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {!rows.length ? (
                        <tr><td colSpan={CLIENT_COLUMNS.length} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                    ) : rows.map((r) => {
                        // traffic in the previous period but none now = stopped using the API
                        let stopped = !r.requests && r.previous && r.previous.requests > 0;
                        let rowStyle = stopped ? { color: palette.textMuted } : undefined;

                        return (
                            <tr key={r.clientId} style={rowStyle}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button type="button" style={{ ...styles.link, color: stopped ? palette.textMuted : styles.link.color }} onClick={() => onOpenClient && onOpenClient(r)}>
                                        {clientDisplayName(r)}
                                    </button>
                                    {r.customerNumber ? <span style={{ fontSize: 11, color: palette.textMuted, marginLeft: 4 }}>({r.customerNumber})</span> : null}
                                    {r.heavy ? <span style={{ ...styles.badge, ...BADGES.heavy }}>{'Heavy'.translate(lang)}</span> : null}
                                    {r.isNew ? <span style={{ ...styles.badge, ...BADGES.new }}>{'New'.translate(lang)}</span> : null}
                                    {r.authMethod === 'basic' ? <span style={{ ...styles.badge, ...BADGES.basic }}>Basic</span> : null}
                                    {stopped ? <span style={{ ...styles.badge, ...BADGES.stopped }}>{'Stopped'.translate(lang)}</span> : null}
                                </td>
                                <td>{formatNumber(r.requests)}</td>
                                <td>{stopped ? <span style={{ color: palette.textMuted }}>▼ -100%</span> : <Delta value={r.change ? r.change.requestsPercent : null} />}</td>
                                <td>{formatPoints(r.points)}</td>
                                <td>{stopped ? <span style={{ color: palette.textMuted }}>▼ -100%</span> : <Delta value={r.change ? r.change.pointsPercent : null} />}</td>
                                <td>
                                    {formatLoadPercent(r.sharePercent)}
                                    <div style={styles.shareTrack}>
                                        <div style={{ height: '100%', borderRadius: 2, width: `${Math.max(0, Math.min(100, r.sharePercent || 0))}%`, backgroundColor: palette.blue }} />
                                    </div>
                                </td>
                                <td>{fmtDecimal(r.avgPointsPerRequest)}</td>
                                <td>{formatLoadPercent(r.errorPercent)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <span className={loadBadgeClass(r.peakLoadPercent, cost)}>{formatLoadPercent(r.peakLoadPercent)}</span>
                                    {r.peakDay ? <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 2 }}>{moment.utc(r.peakDay).format('YYYY-MM-DD')}</div> : null}
                                </td>
                                <td>{formatNumber(r.overloadMinutes)}</td>
                                <td>{formatNumber(r.activeDays)}</td>
                                <td>{authMethodLabel(r.authMethod, lang)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{r.lastUsedAt ? moment(r.lastUsedAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}

function AuthMigrationCard({ auth, onOpenClient, lang }) {
    if (!auth) return null;

    let total = auth.totalClients || 0;
    let apiKey = auth.apiKey || 0;
    let percent = total ? (apiKey / total) * 100 : 0;
    let stillOnBasic = auth.stillOnBasic || [];

    return (
        <div style={styles.card}>
            <div style={styles.sectionTitle}>{'Moving to API keys'.translate(lang)}</div>
            <div style={{ fontSize: 14, color: palette.textPrimary, marginTop: 8 }}>
                <strong>{formatNumber(apiKey)}</strong> {'of'.translate(lang)} <strong>{formatNumber(total)}</strong> {'clients use an API key'.translate(lang)}
                <span style={{ color: palette.textMuted }}> · Basic: {formatNumber(auth.basic)}</span>
            </div>
            <div style={styles.progressTrack} title={formatLoadPercent(percent)}>
                <div style={{ height: '100%', width: `${percent}%`, backgroundColor: palette.blue }} />
            </div>

            <div style={{ ...styles.sectionTitle, fontSize: 12 }}>
                {`${'Still used Basic in the last'.translate(lang)} ${auth.recentDays || 7} ${'days'.translate(lang)}`} ({formatNumber(stillOnBasic.length)})
            </div>
            <div style={styles.note}>{'Contact these clients before Basic auth is switched off.'.translate(lang)}</div>
            <Table responsive hover size="sm" style={{ marginTop: 8 }}>
                <thead>
                    <tr>
                        <th>{'Client'.translate(lang)}</th>
                        <th>{'Last Basic'.translate(lang)}</th>
                        <th>{'Has an API key too'.translate(lang)}</th>
                    </tr>
                </thead>
                <tbody>
                    {!stillOnBasic.length ? (
                        <tr><td colSpan={3} style={{ color: palette.textMuted }}>{'Nobody - every active client is on an API key.'.translate(lang)}</td></tr>
                    ) : stillOnBasic.map((c) => (
                        <tr key={c.clientId}>
                            <td><button type="button" style={styles.link} onClick={() => onOpenClient && onOpenClient(c)}>{clientDisplayName(c)}</button></td>
                            <td>{c.lastBasicAt ? moment(c.lastBasicAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                            <td>{c.lastApiKeyAt ? `${'Yes'.translate(lang)} (${'last'.translate(lang)} ${moment(c.lastApiKeyAt).format('YYYY-MM-DD')})` : 'No'.translate(lang)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

/**
* @param {{from: string, to: string}} range - inclusive UTC `YYYY-MM-DD` days
* @param {function} colorFor - page-level clientId -> color registry
* @param {function} [onOpenClient] - called with a client row ({clientId, name, ...}) when the user clicks one
*/
function ExternalApiOverview({ lang, range, colorFor, onOpenClient }) {
    let [data, setData] = useState(null);
    let [previousDaily, setPreviousDaily] = useState(null);
    let [loading, setLoading] = useState(true);
    let [error, setError] = useState(null);
    let [metric, setMetric] = useState('requests');
    let [stackMetric, setStackMetric] = useState('points');
    let [compare, setCompare] = useState(false);

    let from = range ? range.from : null;
    let to = range ? range.to : null;

    useEffect(() => {
        if (!from || !to) {
            setLoading(false);
            setData(null);
            return;
        }

        setLoading(true);
        setError(null);
        setPreviousDaily(null);

        fetchExternalApiOverview({ from, to, top: OVERVIEW_TOP_CLIENTS }).then(({ data: result, error: err }) => {
            setLoading(false);
            setData(err ? null : result);
            setError(err);
        });
    }, [from, to]);

    // the previous period only comes back as totals, so its daily line is a
    // second, on-demand call for that range
    let previousRange = data && data.previousRange;
    useEffect(() => {
        if (!compare || !previousRange || !previousRange.from || !previousRange.to) return;

        fetchExternalApiOverview({ from: previousRange.from, to: previousRange.to, top: 1 }).then(({ data: result }) => {
            setPreviousDaily(result ? result.daily : null);
        });
    }, [compare, previousRange]);

    if (loading) {
        return <div style={styles.emptyState}>{'Loading...'.translate(lang)}</div>;
    }

    if (error) {
        return <Alert color="danger">{error}</Alert>;
    }

    if (!data) {
        return null;
    }

    let totals = data.totals || {};
    let change = data.change || {};
    let showDataSince = data.dataSince && data.range && data.range.from < data.dataSince;

    return (
        <div>
            <div style={styles.kpiRow}>
                <KpiTile label={'Requests'.translate(lang)} value={formatNumber(totals.requests)} delta={<Delta value={change.requestsPercent} />} />
                <KpiTile label={'Points'.translate(lang)} value={formatPoints(totals.points)} delta={<Delta value={change.pointsPercent} />} />
                <KpiTile
                    label={'Errors'.translate(lang)}
                    value={formatNumber(totals.errors)}
                    delta={<Delta value={change.errorsPercent} inverse />}
                    sub={formatLoadPercent(totals.errorPercent)}
                />
                <KpiTile
                    label={'Active clients'.translate(lang)}
                    value={formatNumber(totals.activeClients)}
                    delta={<Delta value={change.activeClients} suffix="" />}
                    sub={`${'new'.translate(lang)}: ${formatNumber(totals.newClients)}`}
                />
                <KpiTile label={'Heavy clients'.translate(lang)} value={formatNumber(totals.heavyClients)} />
                <KpiTile
                    label={'Overloaded minutes'.translate(lang)}
                    value={formatNumber(totals.overloadMinutes)}
                    title={'Peak load and overloaded minutes show problems; the 24 h average load is always small.'.translate(lang)}
                />
            </div>

            {showDataSince ? (
                <Alert color="info">
                    {`${'Counters exist since'.translate(lang)} ${data.dataSince}. ${'Days before that are 0 because they were not counted, not because there was no traffic.'.translate(lang)}`}
                </Alert>
            ) : null}

            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.sectionTitle}>{'Per day'.translate(lang)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12, color: palette.textSecondary, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
                            {'Compare with previous period'.translate(lang)}
                        </label>
                        <div className="table-tabs" style={{ marginBottom: 0 }}>
                            {DAILY_METRICS.map((m) => (
                                <button key={m.key} className={metric === m.key ? 'active' : ''} onClick={() => setMetric(m.key)}>{m.label.translate(lang)}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <DailyChart daily={data.daily} previousDaily={compare ? previousDaily : null} metric={metric} lang={lang} />
                <div style={styles.note}>
                    {'"Worst single-client minute" in the tooltip is the highest load one client made in a single minute that day - not the whole API, clients peak at different minutes.'.translate(lang)}
                </div>
            </div>

            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.sectionTitle}>{'Who uses it'.translate(lang)}</div>
                    <div className="table-tabs" style={{ marginBottom: 0 }}>
                        <button className={stackMetric === 'requests' ? 'active' : ''} onClick={() => setStackMetric('requests')}>{'Requests'.translate(lang)}</button>
                        <button className={stackMetric === 'points' ? 'active' : ''} onClick={() => setStackMetric('points')}>{'Points'.translate(lang)}</button>
                    </div>
                </div>
                <ClientStackChart dailyByClient={data.dailyByClient} metric={stackMetric} colorFor={colorFor} onOpenClient={onOpenClient} lang={lang} />
            </div>

            <ClientsTable clients={data.clients} cost={data.cost} onOpenClient={onOpenClient} lang={lang} />

            <AuthMigrationCard auth={data.auth} onOpenClient={onOpenClient} lang={lang} />
        </div>
    );
}

export default ExternalApiOverview;
